/**
 * A small always-on record of what cell residency decided and why.
 *
 * Residency has no visible readout: cells load and drop in response to panning,
 * and when it goes wrong the only symptom is chart detail behaving strangely.
 * The dev build logs every plan to the console, but the console does not exist
 * on a phone, and the phone is where the two residency bugs so far have actually
 * bitten. Both cost a full diagnostic round-trip (screenshots, guesswork, a
 * headless reproduction) that this buffer would have answered in one bug report.
 *
 * So the last plans are kept here, always, and the in-app feedback tool attaches
 * them to every report. The cost is a few dozen short strings in memory, which
 * is nothing against a single chart cell.
 */
import { APP_VERSION, APP_COMMIT } from '@/update/appVersion';

const MAX_LINES = 40;

const lines: string[] = [];

/** Stamped once per app run, so a report says exactly which build produced it. */
let stampedVersion = false;

export function traceResidency(line: string): void {
  if (!stampedVersion) {
    stampedVersion = true;
    lines.push(`build ${APP_VERSION.versionName} (${APP_VERSION.versionCode}) commit ${APP_COMMIT}`);
  }
  // Wall-clock, seconds precision: enough to line a plan up against a screenshot.
  const t = new Date();
  const hh = String(t.getHours()).padStart(2, '0');
  const mm = String(t.getMinutes()).padStart(2, '0');
  const ss = String(t.getSeconds()).padStart(2, '0');
  lines.push(`${hh}:${mm}:${ss} ${line}`);
  if (lines.length > MAX_LINES) lines.splice(0, lines.length - MAX_LINES);
}

/** The buffer, oldest first. A copy, so a report cannot be mutated afterwards. */
export function residencyTraceSnapshot(): string[] {
  return [...lines];
}
